import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const EventRegistrationFlow = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { token, user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1); // 1-5
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Data
  const [event, setEvent] = useState(null);
  const [series, setSeries] = useState([]);
  const [kompetisi, setKompetisi] = useState([]);
  
  // Selection
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [selectedKompetisi, setSelectedKompetisi] = useState(null);
  const [selectedRole, setSelectedRole] = useState(''); // siswa, kapten, guru
  
  // Form Data
  const [formData, setFormData] = useState({
    nama_team: '',
    nama_sekolah: user?.m_sekolah_id || '',
    penanggungjawab_nama: '',
    penanggungjawab_hp: '',
    surat_rekomendasi_url: '',
    surat_pernyataan_url: '',
    ktp_kartu_pelajar_url: '',
    my_team: [], // [{foto_url: '', nama: '', role: 'player/official/koordinator/crew'}]
  });

  useEffect(() => {
    if (eventId) {
      fetchEventData();
    }
  }, [eventId]);

  useEffect(() => {
    if (selectedSeries) {
      fetchKompetisi();
    }
  }, [selectedSeries]);

  const fetchEventData = async () => {
    try {
      const [eventRes, seriesRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/events/${eventId}/series`)
      ]);
      
      setEvent(eventRes.data);
      setSeries(seriesRes.data);
    } catch (error) {
      toast.error('Gagal memuat data event');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchKompetisi = async () => {
    try {
      const response = await axios.get(`${API}/series/${selectedSeries.id}/kompetisi`);
      setKompetisi(response.data);
    } catch (error) {
      toast.error('Gagal memuat kompetisi');
    }
  };

  const handleNext = () => {
    // Validation
    if (currentStep === 2 && !selectedSeries) {
      toast.error('Pilih series terlebih dahulu');
      return;
    }
    if (currentStep === 3 && !selectedKompetisi) {
      toast.error('Pilih kompetisi terlebih dahulu');
      return;
    }
    if (currentStep === 4 && !selectedRole) {
      toast.error('Pilih role terlebih dahulu');
      return;
    }
    
    setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      toast('Uploading...');
      const response = await axios.post(`${API}/upload/document`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, [fieldName]: response.data.url });
      toast.success('File berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload file');
    }
  };

  const handleTeamPhotoUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/team-photo`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newTeam = [...formData.my_team];
      newTeam[index].foto_url = response.data.url;
      setFormData({ ...formData, my_team: newTeam });
      toast.success('Foto berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload foto');
    }
  };

  const addTeamMember = (memberType = 'player') => {
    setFormData({
      ...formData,
      my_team: [...formData.my_team, { foto_url: '', nama: '', role: memberType }]
    });
  };

  const removeTeamMember = (index) => {
    const newTeam = formData.my_team.filter((_, i) => i !== index);
    setFormData({ ...formData, my_team: newTeam });
  };

  const updateTeamMember = (index, field, value) => {
    const newTeam = [...formData.my_team];
    newTeam[index][field] = value;
    setFormData({ ...formData, my_team: newTeam });
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.nama_team || !formData.nama_sekolah || !formData.penanggungjawab_nama || !formData.penanggungjawab_hp) {
      toast.error('Lengkapi semua field yang wajib diisi');
      return;
    }

    if (selectedRole !== 'siswa') {
      if (!formData.surat_rekomendasi_url || !formData.surat_pernyataan_url || !formData.ktp_kartu_pelajar_url) {
        toast.error('Upload semua dokumen yang diperlukan');
        return;
      }

      if (formData.my_team.length === 0) {
        toast.error('Tambahkan minimal 1 anggota tim');
        return;
      }

      // Validate MY TEAM based on kompetisi type
      const jenis = selectedKompetisi.jenis.toLowerCase();
      if (jenis === 'futsal' && formData.my_team.length > 12) {
        toast.error('Anggota tim futsal maksimal 12 orang');
        return;
      }
      if (jenis === 'dance' && (formData.my_team.length < 6 || formData.my_team.length > 12)) {
        toast.error('Anggota tim dance minimal 6 dan maksimal 12 orang');
        return;
      }
      if (jenis === 'skill' && formData.my_team.length > 5) {
        toast.error('Anggota tim skill maksimal 5 orang');
        return;
      }
    }

    setSubmitting(true);

    try {
      // Submit registrasi
      const registrationData = {
        m_event_id: eventId,
        m_series_id: selectedSeries.id,
        m_kompetisi_id: selectedKompetisi.id,
        role_kompetisi: selectedRole,
        data_utama: {
          nama_team: formData.nama_team,
          nama_sekolah: formData.nama_sekolah,
          penanggungjawab_nama: formData.penanggungjawab_nama,
          penanggungjawab_hp: formData.penanggungjawab_hp,
          surat_rekomendasi_url: formData.surat_rekomendasi_url,
          surat_pernyataan_url: formData.surat_pernyataan_url,
          ktp_kartu_pelajar_url: formData.ktp_kartu_pelajar_url,
          my_team: formData.my_team
        }
      };

      await axios.post(`${API}/registrations`, registrationData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Registrasi berhasil! Data Anda sedang dalam proses validasi.');
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal submit registrasi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-ltb-bright mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-ltb-r text-white py-6 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold">{event?.nama}</h1>
          <p className="text-white/80 mt-1">Registrasi Event</p>
        </div>
      </header>

      {/* Progress Indicator */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center mb-8">
          {['Booklet', 'Series', 'Kompetisi', 'Role', 'Data'].map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                  currentStep === index + 1 
                    ? 'bg-ltb-bright text-white' 
                    : currentStep > index + 1
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}>
                  {currentStep > index + 1 ? '✓' : index + 1}
                </div>
                <span className={`text-xs mt-2 ${
                  currentStep === index + 1 ? 'text-ltb-bright font-semibold' : 'text-gray-500'
                }`}>
                  {step}
                </span>
              </div>
              {index < 4 && (
                <div className={`w-16 h-1 mx-2 ${
                  currentStep > index + 1 ? 'bg-green-500' : 'bg-gray-300'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {currentStep === 1 && <Step1Booklet event={event} />}
          {currentStep === 2 && <Step2Series series={series} selectedSeries={selectedSeries} setSelectedSeries={setSelectedSeries} />}
          {currentStep === 3 && <Step3Kompetisi kompetisi={kompetisi} selectedKompetisi={selectedKompetisi} setSelectedKompetisi={setSelectedKompetisi} />}
          {currentStep === 4 && <Step4Role selectedRole={selectedRole} setSelectedRole={setSelectedRole} />}
          {currentStep === 5 && (
            <Step5Data 
              selectedRole={selectedRole}
              selectedKompetisi={selectedKompetisi}
              formData={formData}
              setFormData={setFormData}
              handleFileUpload={handleFileUpload}
              handleTeamPhotoUpload={handleTeamPhotoUpload}
              addTeamMember={addTeamMember}
              removeTeamMember={removeTeamMember}
              updateTeamMember={updateTeamMember}
            />
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          {currentStep > 1 && (
            <button
              onClick={handlePrev}
              className="px-6 py-3 border-2 border-ltb-bright text-ltb-bright rounded-lg font-semibold hover:bg-ltb-bright hover:text-white transition-all"
            >
              ← Kembali
            </button>
          )}
          
          <div className="ml-auto">
            {currentStep < 5 ? (
              <button
                onClick={handleNext}
                className="px-6 py-3 bg-gradient-ltb-r text-white rounded-lg font-semibold hover:shadow-xl transition-all"
              >
                Berikutnya →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-8 py-3 bg-gradient-ltb-r text-white rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Registrasi'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components
const Step1Booklet = ({ event }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-800">Booklet Event</h2>
    <p className="text-gray-600">Silakan baca booklet event sebelum melanjutkan registrasi</p>
    
    {event?.dokumen_booklet_url ? (
      <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
        <iframe 
          src={event.dokumen_booklet_url}
          className="w-full h-[600px]"
          title="Event Booklet"
        />
      </div>
    ) : (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-gray-500">Booklet tidak tersedia</p>
      </div>
    )}
  </div>
);

const Step2Series = ({ series, selectedSeries, setSelectedSeries }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-800">Pilih Series</h2>
    <p className="text-gray-600">Pilih series yang ingin Anda ikuti</p>
    
    <div className="grid gap-4">
      {series.map((s) => (
        <button
          key={s.id}
          onClick={() => setSelectedSeries(s)}
          className={`p-6 border-2 rounded-lg text-left transition-all ${
            selectedSeries?.id === s.id
              ? 'border-ltb-bright bg-ltb-bright/10'
              : 'border-gray-300 hover:border-ltb-bright'
          }`}
        >
          <h3 className="text-lg font-bold text-gray-800">{s.nama_series}</h3>
          {s.deskripsi && <p className="text-gray-600 mt-2">{s.deskripsi}</p>}
        </button>
      ))}
    </div>
  </div>
);

const Step3Kompetisi = ({ kompetisi, selectedKompetisi, setSelectedKompetisi }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-800">Pilih Kompetisi</h2>
    <p className="text-gray-600">Pilih kompetisi yang ingin Anda ikuti</p>
    
    <div className="grid gap-4">
      {kompetisi.map((k) => (
        <button
          key={k.id}
          onClick={() => setSelectedKompetisi(k)}
          className={`p-6 border-2 rounded-lg text-left transition-all ${
            selectedKompetisi?.id === k.id
              ? 'border-ltb-bright bg-ltb-bright/10'
              : 'border-gray-300 hover:border-ltb-bright'
          }`}
        >
          <h3 className="text-lg font-bold text-gray-800">{k.nama_kompetisi}</h3>
          <p className="text-sm text-gray-500 mt-1">Jenis: {k.jenis}</p>
          {k.deskripsi && <p className="text-gray-600 mt-2">{k.deskripsi}</p>}
        </button>
      ))}
    </div>
  </div>
);

const Step4Role = ({ selectedRole, setSelectedRole }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-800">Pilih Role</h2>
    <p className="text-gray-600">Pilih role Anda dalam kompetisi ini</p>
    
    <div className="grid grid-cols-3 gap-4">
      {['siswa', 'kapten', 'guru'].map((role) => (
        <button
          key={role}
          onClick={() => setSelectedRole(role)}
          className={`p-6 border-2 rounded-lg text-center transition-all ${
            selectedRole === role
              ? 'border-ltb-bright bg-ltb-bright text-white'
              : 'border-gray-300 hover:border-ltb-bright'
          }`}
        >
          <div className="text-3xl mb-2">
            {role === 'siswa' ? '🎓' : role === 'kapten' ? '👨‍✈️' : '👨‍🏫'}
          </div>
          <div className="font-bold capitalize">{role}</div>
        </button>
      ))}
    </div>
  </div>
);

const Step5Data = ({ 
  selectedRole, 
  selectedKompetisi, 
  formData, 
  setFormData,
  handleFileUpload,
  handleTeamPhotoUpload,
  addTeamMember,
  removeTeamMember,
  updateTeamMember
}) => {
  const isKaptenOrGuru = selectedRole === 'kapten' || selectedRole === 'guru';
  const jenis = selectedKompetisi?.jenis?.toLowerCase();

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Isi Data Registrasi</h2>
      
      {selectedRole === 'siswa' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-800 font-semibold">Role Siswa</p>
          <p className="text-blue-600 mt-2">Siswa hanya perlu menunggu undangan dari Kapten/Guru untuk bergabung dalam tim.</p>
        </div>
      ) : (
        <>
          {/* Dokumen Upload */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-700">Dokumen <span className="text-red-500">*</span></h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Surat Rekomendasi Sekolah (PDF) <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => handleFileUpload(e, 'surat_rekomendasi_url')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {formData.surat_rekomendasi_url && (
                <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Surat Pernyataan Ketersediaan Peserta (PDF) <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-2 mb-2">
                <a
                  href="https://example.com/template-surat-pernyataan.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-ltb-bright hover:underline"
                >
                  📥 Download Template
                </a>
              </div>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => handleFileUpload(e, 'surat_pernyataan_url')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {formData.surat_pernyataan_url && (
                <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                KTP (Official) & Kartu Pelajar (1 PDF) <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => handleFileUpload(e, 'ktp_kartu_pelajar_url')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {formData.ktp_kartu_pelajar_url && (
                <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
              )}
            </div>
          </div>

          {/* Tim Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-700">Informasi Tim <span className="text-red-500">*</span></h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Team <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nama_team}
                onChange={(e) => setFormData({ ...formData, nama_team: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Masukkan nama tim"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Sekolah <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nama_sekolah}
                onChange={(e) => setFormData({ ...formData, nama_sekolah: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Masukkan nama sekolah"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Penanggungjawab <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.penanggungjawab_nama}
                onChange={(e) => setFormData({ ...formData, penanggungjawab_nama: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Masukkan nama penanggungjawab"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                No. HP Penanggungjawab <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.penanggungjawab_hp}
                onChange={(e) => setFormData({ ...formData, penanggungjawab_hp: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="08xxxxxxxxxx"
              />
            </div>
          </div>

          {/* MY TEAM */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-700">MY TEAM <span className="text-red-500">*</span></h3>
              <button
                onClick={() => addTeamMember('player')}
                className="bg-ltb-bright text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg transition-all"
              >
                + Tambah Anggota
              </button>
            </div>

            {jenis === 'futsal' && (
              <p className="text-sm text-gray-600">Maksimal 12 anggota + 2 official + 1 koordinator supporter</p>
            )}
            {jenis === 'dance' && (
              <p className="text-sm text-gray-600">Minimal 6, Maksimal 12 anggota + 4 supporting crew</p>
            )}
            {jenis === 'skill' && (
              <p className="text-sm text-gray-600">Maksimal 5 anggota + 1 official</p>
            )}

            {formData.my_team.map((member, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Foto</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleTeamPhotoUpload(e, index)}
                      className="w-full text-sm"
                    />
                    {member.foto_url && (
                      <img src={member.foto_url} alt="Preview" className="mt-2 h-16 w-16 object-cover rounded" />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nama</label>
                    <input
                      type="text"
                      value={member.nama}
                      onChange={(e) => updateTeamMember(index, 'nama', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Nama anggota"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      value={member.role}
                      onChange={(e) => updateTeamMember(index, 'role', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="player">Player</option>
                      <option value="official">Official</option>
                      {jenis === 'futsal' && <option value="koordinator">Koordinator Supporter</option>}
                      {jenis === 'dance' && <option value="crew">Supporting Crew</option>}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => removeTeamMember(index)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition-all"
                    >
                      Hapus
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EventRegistrationFlow;
